"use client";

import { useContext, useMemo } from "react";
import { CardWidthContext } from "@/utils/CardWidthProvider";
import { type DeepReadonly } from "@/utils/tsmagic";
import DOMPurify from "isomorphic-dompurify";
import { Sequence } from "remotion";
import { type z } from "zod";

import { type RichTextCardObjectSchema } from "../core/derivedSchema";
import { useCurrentFrameStyle } from "../core/useCurrentFrameStyle";
import { resolveRichTextWidthPx, richTextWhiteSpace } from "./richTextLayout";

export type RichTextCardObject = z.infer<typeof RichTextCardObjectSchema>;
export type ReadOnlyRichTextCardObject = DeepReadonly<RichTextCardObject>;

export const RichTextCardObjectComponent: React.FC<{
  cardObject: ReadOnlyRichTextCardObject;
}> = ({ cardObject }) => {
  const { outerStyle, innerStyle } = useCurrentFrameStyle(cardObject);
  const cardWidthContext = useContext(CardWidthContext);
  const width = resolveRichTextWidthPx(cardObject.width, cardWidthContext?.width);
  const html = useMemo(
    () => DOMPurify.sanitize(cardObject.metadata.rawHTML),
    [cardObject.metadata.rawHTML],
  );
  return (
    <Sequence
      from={cardObject.sequence.from}
      durationInFrames={cardObject.sequence.durationInFrames}
      layout="none"
    >
      <div style={outerStyle}>
        <div
          dangerouslySetInnerHTML={{ __html: html }}
          style={{
            width,
            // Wrapping is opt-in per object via `metadata.wrap` (default off),
            // so every existing/sent object keeps its current single-line
            // `nowrap` render — nothing changes retroactively. When an artist
            // turns wrap on, text wraps within the width box; overflowWrap
            // stops a single long word from bleeding past it. Overflow policy:
            // we do NOT clip (no overflow:hidden) — clipping would drop words.
            // (text-rendering & typography LLD §6.1.)
            whiteSpace: richTextWhiteSpace(cardObject.metadata.wrap),
            overflowWrap: "break-word",
            ...innerStyle,
          }}
        />
      </div>
    </Sequence>
  );
};
